import { useEffect, useState } from 'react';
import {
  apiPost,
  decodeCreationOptions,
  decodeRequestOptions,
  encodeCredential,
  probeEnvironment,
  remediationFor,
} from './engine/webauthnEngine';
import { audience, copyFor, successCopy } from './engine/platformCopy';
import { DEFAULT_CEREMONY, toRegisterOptions } from './engine/ceremonyOptions';
import {
  lookupAaguid,
  meaningAlgorithm,
  meaningAttachment,
  meaningBackupEligible,
  meaningBackedUp,
  meaningDiscoverable,
  meaningMethod,
  meaningStoreClass,
  meaningUv,
} from './engine/lookups';

const STEPS = [
  { id: 'secure-context', label: 'Secure context (HTTPS)' },
  { id: 'webauthn-api', label: 'WebAuthn API available' },
  { id: 'platform-authenticator', label: 'Platform authenticator (UV)' },
  { id: 'platform-create', label: 'Create platform passkey' },
  { id: 'passkey-store', label: 'Classify passkey store' },
  { id: 'platform-get', label: 'Sign with platform passkey' },
];

function App() {
  const [view, setView] = useState('landing'); // landing | running | failed | success
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [stepStatus, setStepStatus] = useState({});
  const [remediation, setRemediation] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ceremony, setCeremony] = useState(DEFAULT_CEREMONY);
  const [platform, setPlatform] = useState({ os: '', browser: '' });
  const ui = copyFor(platform.os, platform.browser);

  const setOpt = (key, value) => {
    setCeremony((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    probeEnvironment().then((env) => {
      setPlatform({ os: env.os, browser: env.browser });
    }).catch(() => {});
  }, []);

  const validateEmail = (value) => {
    if (!value || !value.includes('@') || value.length > 254) {
      return false;
    }
    return true;
  };

  const setStep = (id, status) => {
    setStepStatus((prev) => ({ ...prev, [id]: status }));
  };

  const runDiagnostics = async () => {
    if (!validateEmail(email)) {
      setEmailError('Enter a valid email.');
      return;
    }
    setEmailError('');
    setBusy(true);
    setView('running');
    setRemediation([]);
    setErrorMessage('');
    setReport(null);
    setStepStatus({});

    // Email stays in React state only — never sent for storage; only used transiently in register/options
    const localEmail = email.trim();
    let env;

    try {
      env = await probeEnvironment();
      setPlatform({ os: env.os, browser: env.browser });

      setStep('secure-context', env.secureContext ? 'pass' : 'fail');
      if (!env.secureContext) {
        return fail(env, null, 'secure-context');
      }

      setStep('webauthn-api', env.webauthnApi ? 'pass' : 'fail');
      if (!env.webauthnApi) {
        return fail(env, null, 'webauthn-api');
      }

      if (ceremony.platform) {
        setStep('platform-authenticator', env.platformAuthenticator ? 'pass' : 'fail');
        if (!env.platformAuthenticator) {
          return fail(env, null, 'platform-authenticator');
        }
      } else {
        setStep('platform-authenticator', 'pass');
      }

      // Session + ceremonies
      const session = await apiPost('/api/session', {});
      const sessionId = session.session_id;
      const options = toRegisterOptions(ceremony);

      setStep('platform-create', 'running');
      const regOptions = await apiPost('/api/register/options', {
        session_id: sessionId,
        email: localEmail,
        cross_platform: options.authenticator_attachment !== 'platform',
        options,
      });

      const publicKey = normalizePublicKey(regOptions.publicKey || regOptions);
      let credential;
      try {
        credential = await navigator.credentials.create({
          publicKey: decodeCreationOptions(publicKey),
        });
      } catch (domErr) {
        setStep('platform-create', 'fail');
        return fail(env, domErr.name, 'platform-create', domErr.message);
      }

      let created;
      try {
        created = await apiPost('/api/register/verify', {
          session_id: sessionId,
          credential: encodeCredential(credential),
        });
      } catch (verifyErr) {
        setStep('platform-create', 'pass');
        setStep('passkey-store', 'fail');
        return fail(env, verifyErr.message, 'passkey-store', verifyErr.message);
      }
      setStep('platform-create', 'pass');
      setStep('passkey-store', created.store?.class === 'hardware_bound' ? 'pass' : 'warn');

      setStep('platform-get', 'running');
      const authOptions = await apiPost('/api/authenticate/options', {
        session_id: sessionId,
      });
      const requestKey = normalizePublicKey(authOptions.publicKey || authOptions);
      let assertion;
      try {
        assertion = await navigator.credentials.get({
          publicKey: decodeRequestOptions(requestKey),
        });
      } catch (domErr) {
        setStep('platform-get', 'fail');
        return fail(env, domErr.name, 'platform-get', domErr.message);
      }

      const asserted = await apiPost('/api/authenticate/verify', {
        session_id: sessionId,
        credential: encodeCredential(assertion),
      });
      setStep('platform-get', 'pass');

      const clientTests = [
        { id: 'secure-context', status: 'pass' },
        { id: 'webauthn-api', status: 'pass' },
        { id: 'platform-authenticator', status: 'pass' },
      ];

      const serverReport = await apiPost('/api/report', {
        session_id: sessionId,
        environment: {
          secureContext: env.secureContext,
          webauthnApi: env.webauthnApi,
          platformAuthenticator: env.platformAuthenticator,
          conditionalMediation: env.conditionalMediation,
          clientCapabilities: env.clientCapabilities,
          os: env.os,
          browser: env.browser,
          userAgent: env.userAgent,
        },
        tests: clientTests,
      });

      // Merge email client-side only for the on-screen / downloaded report (not stored by server)
      const fullReport = {
        ...serverReport,
        local_label: { email: localEmail, note: 'Email held only in this browser session; not stored by IDEE.' },
        ceremony: toRegisterOptions(ceremony),
        register_result: created,
        assert_result: asserted,
        client_credential: {
          id: credential.id,
          authenticatorAttachment: credential.authenticatorAttachment,
          transports: credential.response.getTransports ? credential.response.getTransports() : undefined,
          type: credential.type,
        },
      };
      setReport(fullReport);
      setView('success');
    } catch (e) {
      if (e.status === 429 || e.message === 'rate_limited') {
        setErrorMessage('Too many requests from this network. Please wait and try again.');
        setRemediation([{
          title: 'Rate limited',
          steps: ['Wait a few minutes before running diagnostics again.', 'This protects the public doctor service from abuse.'],
        }]);
        setView('failed');
      } else {
        setErrorMessage(e.message || 'Diagnostics failed');
        setRemediation(remediationFor({ env, errorName: e.name || e.message, failedStep: 'request' }));
        setView('failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const fail = (env, errorName, failedStep, message) => {
    setErrorMessage(message || `Check failed: ${failedStep}`);
    setRemediation(remediationFor({ env, errorName, failedStep }));
    setView('failed');
    setBusy(false);
  };

  const downloadJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.report_id || 'webauthn-doctor-report'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setView('landing');
    setStepStatus({});
    setRemediation([]);
    setErrorMessage('');
    setReport(null);
  };

  return (
    <div className="page">
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <img src={`${process.env.PUBLIC_URL}/company-logo.svg`} alt="IDEE" />
          </div>
          <div className="topbar-badge">Zero-knowledge &amp; Zero-PII</div>
        </div>
      </header>

      <section className="hero">
        <div className="hero-inner">
          <p className="eyebrow">MFA 2.0 READINESS UTILITY</p>
          <h1>Web-AuthN Doctor</h1>
          <p className="subtitle">
            {ui.hero}
          </p>
          {platform.os && (
            <p className="hero-platform">{audience(platform.os, platform.browser)}</p>
          )}
        </div>
      </section>

      <main className="main">
        <section className="privacy-banner">
          <strong>Zero-knowledge &amp; Zero-PII.</strong>{' '}
          IDEE does not store your email or any personal data from this tool.
          Your email is only used in this browser and is never persisted on our servers.
          Lab credentials are ephemeral and non-identifying.
        </section>

        {view === 'landing' && (
          <section className="card">
            <h2>Run diagnostics on this machine</h2>
            <p className="muted">
              No IDEE account required. {ui.intro}
            </p>
            <label className="field">
              <span>User email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                autoComplete="email"
              />
              {emailError && <span className="field-error">{emailError}</span>}
            </label>
            <p className="hint">
              {ui.hint}
            </p>
            <fieldset className="options">
              <legend>Create options</legend>
              <p className="options-lead">Defaults match IDEE enrollment. Uncheck anything you want to drop from this test.</p>
              <div className="options-grid">
                <div className="options-col">
                  <p className="options-label">Authenticator</p>
                  <Check label="This device (platform)" checked={ceremony.platform} onChange={(v) => setOpt('platform', v)} />
                  <Check label="Security key or phone" checked={ceremony.crossPlatform} onChange={(v) => setOpt('crossPlatform', v)} />
                </div>
                <div className="options-col">
                  <p className="options-label">Verification</p>
                  <Check label="Require user verification" checked={ceremony.userVerification} onChange={(v) => setOpt('userVerification', v)} />
                  <Check label="Discoverable passkey" checked={ceremony.residentKey} onChange={(v) => setOpt('residentKey', v)} />
                </div>
                <div className="options-col">
                  <p className="options-label">Algorithms</p>
                  <Check label="ES256" checked={ceremony.algEs256} onChange={(v) => setOpt('algEs256', v)} />
                  <Check label="RS256" checked={ceremony.algRs256} onChange={(v) => setOpt('algRs256', v)} />
                  <Check label="EdDSA" checked={ceremony.algEddsa} onChange={(v) => setOpt('algEddsa', v)} />
                </div>
                <div className="options-col">
                  <p className="options-label">Extensions</p>
                  <Check label="credProps" checked={ceremony.credProps} onChange={(v) => setOpt('credProps', v)} />
                  <Check label="PRF" checked={ceremony.prf} onChange={(v) => setOpt('prf', v)} />
                  <Check label="Attestation (direct)" checked={ceremony.attestation} onChange={(v) => setOpt('attestation', v)} />
                </div>
              </div>
            </fieldset>
            <button type="button" className="btn primary" onClick={runDiagnostics} disabled={busy}>
              Run diagnostics
            </button>
          </section>
        )}

        {(view === 'running' || view === 'failed') && (
          <section className="card">
            <h2>{view === 'running' ? 'Running checks…' : 'Issues found'}</h2>
            <ul className="steps">
              {STEPS.map((s) => (
                <li key={s.id} className={`step step-${stepStatus[s.id] || 'pending'}`}>
                  <span className="step-dot" />
                  <span>{s.label}</span>
                  <span className="step-status">{labelStatus(stepStatus[s.id])}</span>
                </li>
              ))}
            </ul>
            {view === 'running' && (
              <p className="muted">{ui.prompt}</p>
            )}
            {view === 'failed' && (
              <>
                {errorMessage && <p className="error-text">{errorMessage}</p>}
                <div className="remediation">
                  {remediation.map((r) => (
                    <div key={r.title} className="remediation-card">
                      <h3>{r.title}</h3>
                      <ol>
                        {r.steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
                <div className="actions">
                  <button type="button" className="btn primary" onClick={runDiagnostics} disabled={busy}>
                    Re-test
                  </button>
                  <button type="button" className="btn ghost" onClick={reset}>
                    Start over
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        {view === 'success' && report && (
          <section className="profile">
            <div className="profile-header">
              <div>
                <p className="eyebrow">FULL CEREMONY DUMP · ADMIN / IDEE TECH</p>
                <h2>{successCopy(report).title}</h2>
                <p className="scope">
                  {audience(report.environment?.os, report.environment?.browser)} · {report.verified_at}
                </p>
              </div>
              <div className={successBadgeClass(report)}>
                {successCopy(report).badge}
              </div>
            </div>

            <div className={`card store-banner ${report.mfa20_hardware_bound ? 'store-ok' : 'store-warn'}`}>
              <h3>Store</h3>
              <p className="pill">{aaguidInfo(report).name} · {aaguidInfo(report).vendor}</p>
              <p>{successCopy(report).body}</p>
            </div>

            <div className="card">
              <h3>Authenticator / AAGUID</h3>
              <dl className="dl wide">
                <TechRow
                  label="AAGUID"
                  meaning={`${aaguidInfo(report).name} (${aaguidInfo(report).vendor})`}
                  raw={report.credential?.aaguid_uuid || report.store?.aaguid || report.credential?.aaguid}
                  note={aaguidInfo(report).note}
                />
                <TechRow
                  label="AAGUID (base64url)"
                  meaning="Raw bytes as returned by Yubico finishRegistration"
                  raw={report.credential?.aaguid}
                />
                <TechRow
                  label="How classified"
                  meaning={meaningMethod(report.store?.method || report.credential?.classification_method)}
                  raw={report.store?.method || report.credential?.classification_method}
                />
                <TechRow
                  label="Confidence"
                  meaning={report.store?.confidence === 'high' ? 'Catalog match or Safari-only path' : report.store?.confidence === 'medium' ? 'Inferred from OS/browser + flags' : 'Low — treat as unclassified'}
                  raw={report.store?.confidence || report.credential?.classification_confidence}
                />
                <TechRow
                  label="Store class"
                  meaning={meaningStoreClass(report.store?.class || report.credential?.store_class)}
                  raw={report.store?.class || report.credential?.store_class}
                />
                <TechRow
                  label="Vendor"
                  meaning={report.store?.vendor || report.credential?.vendor}
                  raw={report.store?.vendor || report.credential?.vendor}
                />
              </dl>
            </div>

            <div className="grid">
              <div className="card">
                <h3>Flags</h3>
                <dl className="dl wide">
                  <TechRow label="Backup eligible (BE)" meaning={meaningBackupEligible(report.credential?.backup_eligible)} raw={formatFlag(report.credential?.backup_eligible)} />
                  <TechRow label="Backed up (BS)" meaning={meaningBackedUp(report.credential?.backed_up)} raw={formatFlag(report.credential?.backed_up)} />
                  <TechRow label="User verification" meaning={meaningUv(report.credential?.user_verification)} raw={formatFlag(report.credential?.user_verification)} />
                  <TechRow label="Discoverable (rk)" meaning={meaningDiscoverable(report.credential?.discoverable)} raw={formatFlag(report.credential?.discoverable)} />
                  <TechRow label="Attachment" meaning={meaningAttachment(report.credential?.attachment)} raw={report.credential?.attachment} />
                  <TechRow label="Transports" meaning="internal = this device; usb/nfc/ble = security key; hybrid = phone" raw={formatList(report.client_credential?.transports)} />
                  <TechRow label="Algorithm" meaning={meaningAlgorithm(report.credential?.algorithm)} raw={report.credential?.algorithm} />
                  <TechRow label="Sign count" meaning="Authenticator signature counter (0 if unused)" raw={report.credential?.sign_count} />
                </dl>
              </div>
              <div className="card">
                <h3>Environment</h3>
                <dl className="dl wide">
                  <TechRow label="OS" meaning={report.environment?.os} raw={report.environment?.os} />
                  <TechRow label="Browser" meaning={report.environment?.browser} raw={report.environment?.browser} />
                  <TechRow label="Secure context" meaning={report.environment?.secureContext ? 'HTTPS / localhost' : 'Not secure'} raw={formatFlag(report.environment?.secureContext)} />
                  <TechRow label="WebAuthn API" meaning="window.PublicKeyCredential" raw={formatFlag(report.environment?.webauthnApi)} />
                  <TechRow label="UV platform" meaning="isUserVerifyingPlatformAuthenticatorAvailable()" raw={formatFlag(report.environment?.platformAuthenticator)} />
                  <TechRow label="Conditional UI" meaning="isConditionalMediationAvailable()" raw={formatFlag(report.environment?.conditionalMediation)} />
                  <TechRow label="User-Agent" meaning="Full client UA string" raw={report.environment?.userAgent} />
                </dl>
              </div>
            </div>

            <div className="card">
              <h3>Create options used</h3>
              <dl className="dl wide">
                <TechRow label="Attachment request" meaning={meaningAttachment(report.ceremony?.authenticator_attachment)} raw={report.ceremony?.authenticator_attachment} />
                <TechRow label="User verification" meaning={report.ceremony?.user_verification} raw={report.ceremony?.user_verification} />
                <TechRow label="Resident key" meaning={report.ceremony?.resident_key} raw={report.ceremony?.resident_key} />
                <TechRow label="Attestation" meaning={report.ceremony?.attestation === 'none' ? 'None — AAGUID may be zeroed by the client' : report.ceremony?.attestation} raw={report.ceremony?.attestation} />
                <TechRow label="Algorithms" meaning={(report.ceremony?.algorithms || []).map(meaningAlgorithm).join(' · ')} raw={(report.ceremony?.algorithms || []).join(', ')} />
                <TechRow label="credProps" meaning="Ask the client if the credential is resident" raw={formatFlag(report.ceremony?.cred_props)} />
                <TechRow label="PRF" meaning="hmac-secret / PRF extension" raw={formatFlag(report.ceremony?.prf)} />
              </dl>
            </div>

            <div className="card">
              <h3>Credential</h3>
              <dl className="dl wide">
                <TechRow label="Credential ID" meaning="Public key credential identifier stored by the IdP" raw={report.credential?.credential_id} />
                <TechRow label="Type" meaning={report.client_credential?.type} raw={report.client_credential?.type} />
                <TechRow label="Client attachment" meaning={meaningAttachment(report.client_credential?.authenticatorAttachment)} raw={report.client_credential?.authenticatorAttachment} />
                <TechRow label="Report ID" meaning="Ephemeral lab report id (not an identity)" raw={report.report_id} />
                <TechRow label="Status" meaning={report.status} raw={report.status} />
                <TechRow label="Label (browser only)" meaning="Not stored on the server" raw={report.local_label?.email} />
              </dl>
            </div>

            <div className="card nist">
              <h3>NIST SP 800-63B (device scope)</h3>
              <ul className="check-list">
                {(report.framework_alignment?.nist_sp_800_63b?.items || []).map((item) => (
                  <li key={item.id} className={item.met ? 'met' : 'unmet'}>
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card">
              <h3>Client capabilities</h3>
              <pre className="dump">{pretty(report.environment?.clientCapabilities)}</pre>
              <h3>Register result</h3>
              <pre className="dump">{pretty(report.register_result)}</pre>
              <h3>Assert result</h3>
              <pre className="dump">{pretty(report.assert_result)}</pre>
              <h3>Full report JSON</h3>
              <pre className="dump">{pretty(report)}</pre>
            </div>

            <div className="card meta">
              <div className="actions">
                <button type="button" className="btn primary" onClick={downloadJson}>
                  Download JSON
                </button>
                <button type="button" className="btn ghost" onClick={reset}>
                  Run again
                </button>
              </div>
            </div>

            <p className="disclaimer">
              {report.disclaimer}
            </p>
          </section>
        )}
      </main>

      <footer className="footer">
        <p>Web-AuthN Doctor · IDEE GmbH · Zero-knowledge &amp; Zero-PII</p>
      </footer>
    </div>
  );
}

function labelStatus(status) {
  if (status === 'pass') return 'Pass';
  if (status === 'fail') return 'Fail';
  if (status === 'warn') return 'Warning';
  if (status === 'running') return 'Running…';
  return 'Pending';
}

function formatFlag(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

function formatList(value) {
  if (Array.isArray(value) && value.length) return value.join(', ');
  return '—';
}

function pretty(value) {
  if (value == null) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

function aaguidInfo(report) {
  const uuid = report.credential?.aaguid_uuid || report.store?.aaguid;
  const hit = lookupAaguid(uuid);
  if (report.store?.authenticator && report.store.authenticator !== 'Unknown authenticator') {
    return { ...hit, name: report.store.authenticator, vendor: report.store.vendor || hit.vendor };
  }
  return hit;
}

function TechRow({ label, meaning, raw, note }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        <div className="meaning">{meaning || '—'}</div>
        {raw != null && raw !== '' && raw !== meaning && (
          <div className="mono raw">{String(raw)}</div>
        )}
        {note && <div className="note">{note}</div>}
      </dd>
    </div>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label className="checkbox-field compact">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function successBadgeClass(report) {
  if (report.mfa20_hardware_bound) return 'badge-ok';
  if (report.store_status === 'synced_vault') return 'badge-warn';
  return 'badge-ok';
}

/** Yubico JSON may nest base64 fields; flatten challenge/user.id to base64url strings when needed */
function normalizePublicKey(publicKey) {
  if (!publicKey) return publicKey;
  const pk = { ...publicKey };
  pk.challenge = coerceBase64Url(pk.challenge);
  if (pk.user) {
    pk.user = { ...pk.user, id: coerceBase64Url(pk.user.id) };
  }
  if (pk.excludeCredentials) {
    pk.excludeCredentials = pk.excludeCredentials.map((c) => ({
      ...c,
      id: coerceBase64Url(c.id),
    }));
  }
  if (pk.allowCredentials) {
    pk.allowCredentials = pk.allowCredentials.map((c) => ({
      ...c,
      id: coerceBase64Url(c.id),
    }));
  }
  return pk;
}

function coerceBase64Url(value) {
  if (value == null) return value;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value.base64url) return value.base64url;
    if (value.base64Url) return value.base64Url;
    if (value.base64) return value.base64;
  }
  return value;
}

export default App;
